import { type models } from "jiffy-api";
import { useWatch, useFormState } from "react-hook-form";
import { useSetAtom } from "jotai";
import { useEffect } from "react";

import {
  useArtwork,
  useArtworkForm,
  useClearManualEdits,
  refineArtworkOpenAtom
} from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { getSideFromPlacement, getArtworkIdBySide } from "@design-lab/shared/lib/artworkUtils";
import type { ArtworkForm } from "@design-lab/shared/types";
import {
  trackPrintOptionClicked,
  trackExpertEditAddonToggled,
  trackRemoveBackgroundClicked,
  trackImageEditToolReset
} from "@design-lab/shared/lib/analytics";
import { useShowArtistEditAddon } from "@design-lab/shared/lib/config";
import { QualityTabs } from "./quality-tabs";

export function SelectPrintQuality() {
  const { getArtwork } = useArtwork();
  const { form, lens } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const clearManualEdits = useClearManualEdits();
  const openRefine = useSetAtom(refineArtworkOpenAtom);
  const enableExpertEditAddon = useShowArtistEditAddon();

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const { currentPlacement, info } = artworkForm || {};
  const side = getSideFromPlacement(currentPlacement || "front");
  const artworkInfoForm = info?.[side];
  const artworkIdForm = artworkForm ? getArtworkIdBySide(artworkForm, side) : "";

  const artwork = artworkIdForm ? getArtwork(artworkIdForm) : null;
  const placementSide = side;

  // Watch form values
  const jiffyDesignServices = useWatch({
    control: form.control,
    name: `info.${placementSide}.jiffyDesignServices`
  });

  const editImageManual = useWatch({
    control: form.control,
    name: `info.${placementSide}.editImageManual`
  });

  const removeBackground = useWatch({
    control: form.control,
    name: `info.${placementSide}.removeBackground`
  });

  const enhancementRequestNote = useWatch({
    control: form.control,
    name: `info.${placementSide}.enhancementRequestNote`
  });

  // Get form state for validation
  const fieldState = useFormState({
    control: form.control,
    name: `info.${placementSide}.enhancementRequestNote`
  });
  const instructionsError =
    fieldState.errors?.info?.[placementSide]?.enhancementRequestNote?.message;

  // Register the instructions field
  const instructionsFieldName = `info.${placementSide}.enhancementRequestNote` as const;
  const instructionsField = form.register(instructionsFieldName, {
    validate: value => {
      if (jiffyDesignServices && (!value || !value.trim())) {
        return "Comment is required";
      }
      return true;
    }
  });

  // Set jiffyDesignServices to checked if enhancementRequestNote is present on component load
  useEffect(() => {
    if (enhancementRequestNote && enhancementRequestNote.trim()) {
      form.setValue(`info.${placementSide}.jiffyDesignServices`, true);
    }
  }, [enhancementRequestNote]);

  // Trigger validation when jiffyDesignServices changes
  useEffect(() => {
    if (jiffyDesignServices) {
      form.trigger(instructionsFieldName);
    }
  }, [jiffyDesignServices, form, instructionsFieldName]);

  // Track analytics when jiffyDesignServices is toggled
  useEffect(() => {
    if (jiffyDesignServices === undefined) {
      return;
    }

    const artworkData = form.getValues(placementSide);
    const addedCompositions = compositionForm.getValues("addedCompositions") || [];

    // Extract arrays of composition IDs and catalog IDs
    const compositionIds = addedCompositions.map(comp => comp.id);
    const catalogIds = addedCompositions.map(comp => comp.catalogId);

    trackExpertEditAddonToggled({
      placement: placementSide,
      artworkId: artworkData?.artworkId,
      composition_ids: compositionIds,
      enabled: Boolean(jiffyDesignServices),
      paid_expert_edit_addon: enableExpertEditAddon,
      business_line: "jiffy_made",
      catalog_numbers: catalogIds,
      active_config_section: "full_enhancements",
      source: "design_lab"
    });
  }, [jiffyDesignServices]);

  const handleTabChange = (value: models.cart.madeAdd.EnhancementTier) => {
    const printOptions = {
      print_original: "print_as_is",
      full_enhancements: "print_perfect",
      custom: "artist_edit"
    } as const;

    const printOption = printOptions[value];
    if (!printOption) return;

    trackPrintOptionClicked(printOption);

    form.setValue(`info.${side}.enhancementTier`, value);

    // If switching to "Print As Is", disable remove background
    form.setValue(`info.${side}.removeBackground`, value === "full_enhancements");

    if (value === "print_original") {
      form.setValue(`info.${side}.jiffyDesignServices`, false);
      form.setValue(`info.${side}.enhancementRequestNote`, "");
    }

    // Always clear manual edits when switching Print Quality
    clearManualEdits(side);
  };

  const handleRemoveBackgroundChange = (checked: boolean) => {
    const artworkId = form.getValues(`${placementSide}.artworkId`);
    if (!artworkId) return;

    if (checked) {
      form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
    }
    clearManualEdits(placementSide);

    trackRemoveBackgroundClicked({
      source: "PrintQuality",
      printQuality: "PrintPerfect",
      side: placementSide,
      artworkId
    });

    form.setValue(`info.${placementSide}.removeBackground`, checked);
  };

  const handleJiffyDesignServicesChange = (checked: boolean) => {
    form.setValue(`info.${placementSide}.jiffyDesignServices`, checked);
    if (checked) {
      form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
    }
  };

  const handleEnhancementRequestNoteChange = (value: string) => {
    form.setValue(`info.${placementSide}.enhancementRequestNote`, value);
  };

  const handleRefineArtworkClick = () => {
    openRefine(true);
  };

  const handleResetManualEdit = () => {
    const artworkId = form.getValues(`${placementSide}.artworkId`);
    if (artworkId) {
      trackImageEditToolReset(artworkId);
    }
    clearManualEdits(placementSide);
  };

  return (
    <>
      {artworkInfoForm && (
        <QualityTabs
          key={currentPlacement}
          value={artworkInfoForm.enhancementTier}
          onChange={handleTabChange}
          canRemoveBackground={Boolean(artwork?.canRemoveBg)}
          removeBackground={removeBackground || false}
          jiffyDesignServices={jiffyDesignServices || false}
          enhancementRequestNote={enhancementRequestNote}
          editImageManual={editImageManual || false}
          instructionsError={instructionsError}
          placementSide={placementSide}
          onRemoveBackgroundChange={handleRemoveBackgroundChange}
          onJiffyDesignServicesChange={handleJiffyDesignServicesChange}
          onEnhancementRequestNoteChange={handleEnhancementRequestNoteChange}
          onRefineArtworkClick={handleRefineArtworkClick}
          onResetManualEdit={handleResetManualEdit}
          enhancementRequestNoteField={instructionsField}
          removeBackgroundLens={lens.focus(`info.${placementSide}.removeBackground`)}
        />
      )}
    </>
  );
}
